if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined
      quantityForm = undefined
      onVariantChangeUnsubscriber = undefined
      cartUpdateUnsubscriber = undefined
      abortController = undefined
      pendingRequestUrl = null
      preProcessHtmlCallbacks = []
      postProcessHtmlCallbacks = []
      productJsonCache = {}

      constructor() {
        super()

        this.quantityInput = document.querySelector('.product-form-quantity-button .quantity__input')
      }

      connectedCallback() {
        this.initializeProductSwapUtility()

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        )

        this.initQuantityHandlers()

        // Auto-select first available variant on initial load
        // setTimeout(() => {
        //   this.autoSelectSingleAvailableSize()
        // }, 100)

        this.dispatchEvent(
          new CustomEvent('product-info:loaded', { bubbles: true })
        )
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback)
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return

        this.quantityForm = this.querySelector('.product-form__quantity')
        if (!this.quantityForm) return

        this.setQuantityBoundries()
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          )
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber()
        this.cartUpdateUnsubscriber?.()
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) =>
              element.classList.add('scroll-trigger--cancel')
            )
        )
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
          if (typeof window.yotpoWidgetsContainer !== 'undefined') {
            window.yotpoWidgetsContainer.initWidgets(false);
          }
        })
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return

        this.resetProductFormState()

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url
        this.pendingRequestUrl = productUrl
        const shouldSwapProduct = this.dataset.url !== productUrl
        const shouldFetchFullPage =
          this.dataset.updateUrl === 'true' && shouldSwapProduct

        // For combined listings, fetch target product JSON and resolve URL with correct variant
        if (shouldSwapProduct && this.isCombinedListing()) {
          this.resolveCombinedListingUrlAsync(productUrl, shouldFetchFullPage)
            .then((requestUrl) => {
              this.renderProductInfo({
                requestUrl,
                targetId: target.id,
                callback: this.handleSwapProduct(productUrl, shouldFetchFullPage),
              })
            })
          return
        }

        const requestUrl = this.buildRequestUrlWithParams(
          productUrl,
          selectedOptionValues,
          shouldFetchFullPage
        )

        this.renderProductInfo({
          requestUrl,
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        })
      }

      resetProductFormState() {
        const productForm = this.productForm
        productForm?.toggleSubmitButton(true)
        productForm?.handleErrorMessage()
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove()

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : 'product-info'
          const variant = this.getSelectedVariant(html.querySelector(selector))
          this.updateURL(productUrl, variant?.id)

          if (variant) {
            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            })
          }

          if (updateFullPage) {
            document.querySelector('head title').innerHTML =
              html.querySelector('head title').innerHTML

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            )
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            )
          }
        }
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort()
        this.abortController = new AbortController()

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null
            const html = new DOMParser().parseFromString(
              responseText,
              'text/html'
            )
            callback(html)
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus()
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user')
            } else {
              console.error(error)
            }
          })
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          'variant-selects [data-selected-variant]'
        )?.innerHTML
        return !!selectedVariant ? JSON.parse(selectedVariant) : null
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = []

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`)

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`)
        }

        return `${url}?${params.join('&')}`
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects')
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          )
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html)

          this.pickupAvailability?.update(variant)
          this.updateOptionValues(html)

          // Auto-select single available size when no variant is selected
          // setTimeout(() => {
          //   this.autoSelectSingleAvailableSize()
          // }, 100)

          this.updateURL(productUrl, variant?.id)
          this.updateVariantInputs(variant?.id)

          if (!variant) {
            this.setUnavailable()
            return
          }

          this.updateMedia(html, variant?.featured_media?.id, variant)

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`)
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            )
            if (source && destination) {
              destination.innerHTML = source.innerHTML
              destination.classList.toggle('hidden', shouldHide(source))
            }
          }

          updateSourceFromDestination('Sticky-Atc-Panel');
          updateSourceFromDestination('price')
          updateSourceFromDestination('Sku', ({ classList }) =>
            classList.contains('hidden')
          )
          updateSourceFromDestination(
            'Inventory',
            ({ innerText }) => innerText === ''
          )
          updateSourceFromDestination('Volume')
          updateSourceFromDestination('Price-Per-Item', ({ classList }) =>
            classList.contains('hidden')
          )

          this.updateQuantityRules(this.sectionId, html)
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove('hidden')
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`
          )?.classList.remove('hidden')

          const submitButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`)
          const shouldDisable = submitButton?.hasAttribute('disabled') ?? true

          this.productForm?.toggleSubmitButton(
            shouldDisable,
            window.variantStrings.soldOut
          )

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          })
        }
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]')
          if (input) {
            input.value = variantId ?? ''
            input.dispatchEvent(new Event('change', { bubbles: true }))
          }
        })
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        )

        if (this.dataset.updateUrl === 'false') return
        window.history.replaceState(
          {},
          '',
          `${url}${variantId ? `?variant=${variantId}` : ''}`
        )
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        )

        const selectors = [
          'price',
          'Inventory',
          'Sku',
          'Price-Per-Item',
          'Volume-Note',
          'Volume',
          'Quantity-Rules',
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ')
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add('hidden'))
      }

      updateMedia(html, variantFeaturedMediaId, variant) {
        if (!variantFeaturedMediaId) return

        let mediaGallerySource = this.querySelector('media-gallery ul')
        let mediaGalleryDestination = html.querySelector(`media-gallery ul`)

        if(mediaGallerySource == null) {
          mediaGallerySource = this.querySelector('.desktop-vertical-gallery ul');
          mediaGalleryDestination = html.querySelector(`.desktop-vertical-gallery ul`);
        }

        const thumbnailItems = this.querySelectorAll('.thumbnail-list li');
        const mobileMediaItems = document.querySelectorAll('.product__media-list.slider--mobile li');
        const mediaModalItems = document.querySelectorAll('.product-media-modal__content img');

        const galleryNavItems = document.querySelectorAll('.product-gallery__nav-item');
        const galleryMainImages = document.querySelectorAll('.product-gallery__image');
        const mobileSwiperSlides = document.querySelectorAll('.mobile-vertical-gallery .swiper-slide');

        thumbnailItems.forEach(list => {
          let thumbImage = list.querySelector("img")
          let alt = thumbImage.alt.split("::")[1];

          if(alt == variant.option1) {
            list.classList.remove("hidden")
          }else {
            if(!list.classList.contains("hidden")) {
              list.classList.add("hidden")
            }
          }
        });

        galleryNavItems.forEach(list => {
          list.classList.remove("product-gallery__nav-item--active")
        });


        galleryNavItems.forEach(list => {
          let thumbImage = list.querySelector("img")
          let alt = thumbImage.alt.split("::")[1];
          let mediaId = list.getAttribute('data-media-id');
          let isFeaturedMedia = variant.featured_media && variant.featured_media.id && variant.featured_media.id.toString() === mediaId;

          if(alt == variant.option1 || isFeaturedMedia) {
            list.classList.remove("hidden")
          }else {
            if(!list.classList.contains("hidden")) {
              list.classList.add("hidden")
            }
          }
        });

        const firstVisibleThumb = Array.from(galleryNavItems).find(thumb => !thumb.classList.contains("hidden"));
        if (firstVisibleThumb) {
          firstVisibleThumb.classList.add("product-gallery__nav-item--active");

          const activeMediaId = firstVisibleThumb.getAttribute('data-media-id');
          const activeMainImage = Array.from(galleryMainImages).find(img => img.getAttribute('data-media-id') === activeMediaId);
          if (activeMainImage) {
            galleryMainImages.forEach(img => img.classList.remove("product-gallery__image--active"));
            activeMainImage.classList.add("product-gallery__image--active");
          }
        }

        

        mobileMediaItems.forEach(list => {
          let thumbImage = list.querySelector("img")
          let alt = thumbImage.alt.split("::")[1];

          if(alt == variant.option1) {
            list.classList.remove("hidden")
          }else {
            if(!list.classList.contains("hidden")) {
              list.classList.add("hidden")
            }
          }
        });

        mediaModalItems.forEach(item => {
          let alt = item.alt.split("::")[1];

          if(alt == variant.option1) {
            item.classList.remove("hidden")
          }else {
            if(!item.classList.contains("hidden")) {
              item.classList.add("hidden")
            }
          }
        })

        mobileSwiperSlides.forEach(slide => {
          let thumbImage = slide.querySelector("img");
          if (thumbImage) {
            let alt = thumbImage.alt.split("::")[1];

            if(alt == variant.option1) {
              slide.classList.remove("hidden")
            }else {
              if(!slide.classList.contains("hidden")) {
                slide.classList.add("hidden")
              }
            }
          }
        });

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2)
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll('li[data-media-id]')
          )

          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId)
          )
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ])
          )
          return [mediaGallerySourceItems, sourceSet, sourceMap]
        }

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData()
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          )
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId)
          )
          let shouldRefresh = false

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i])
              shouldRefresh = true
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove()
              shouldRefresh = true
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData()

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId)

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`
                  )
                )

                // refresh source now that it has been modified
                ;[mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData()
              }
            }
          )
        }

        // set featured media as active in the media gallery
        const mediaGallery = this.querySelector(`media-gallery`);
        if (mediaGallery) {
          mediaGallery.setActiveMedia?.(
            `${this.dataset.section}-${variantFeaturedMediaId}`,
            true
          );
        } else {
          const productGallery = this.querySelector('product-gallery');
          if (productGallery) {
            productGallery.setActiveMedia?.(variantFeaturedMediaId, true);
          }
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`
        )
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`
        )
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML
      }

      setQuantityBoundries() {
        if (!this.quantityInput) return

        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        }

        let min = data.min
        const max = data.max === null ? data.max : data.max - data.cartQuantity
        if (max !== null) min = Math.min(min, max)
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step)

        this.quantityInput.min = min

        if (max) {
          this.quantityInput.max = max
        } else {
          this.quantityInput.removeAttribute('max')
        }

        // Only reset value if current value is invalid
        const currentValue = parseInt(this.quantityInput.value) || 0
        if (currentValue < min || (max && currentValue > max)) {
          this.quantityInput.value = min
        }

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined)
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value
        if (!currentVariantId) return

        this.querySelector(
          '.quantity__rules-cart .loading__spinner'
        ).classList.remove('hidden')
        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              'text/html'
            )
            this.updateQuantityRules(this.dataset.section, html)
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              '.quantity__rules-cart .loading__spinner'
            ).classList.add('hidden')
          )
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return

        // Store the current user-entered quantity before updating
        const userQuantity = parseInt(this.quantityInput.value) || 1

        this.setQuantityBoundries()

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        )
        const selectors = [
          '.quantity__input',
          '.quantity__rules',
          '.quantity__label',
        ]
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector)
          const updated = quantityFormUpdated.querySelector(selector)
          if (!current || !updated) continue
          if (selector === '.quantity__input') {
            const attributes = [
              'data-cart-quantity',
              'data-min',
              'data-max',
              'step',
            ]
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute)
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated)
              } else {
                current.removeAttribute(attribute)
              }
            }

            // Restore user quantity if it's still valid after attribute updates
            const newMin = parseInt(current.getAttribute('data-min')) || 1
            const newMax = parseInt(current.getAttribute('data-max')) || null
            if (userQuantity >= newMin && (newMax === null || userQuantity <= newMax)) {
              current.value = userQuantity
            }
          } else {
            current.innerHTML = updated.innerHTML
          }
        }
      }

      isCombinedListing() {
        const variantSelects = this.variantSelectors
        if (!variantSelects) return false
        return variantSelects.hasAttribute('data-combined-listing-product-option-name') &&
               variantSelects.getAttribute('data-combined-listing-product-option-name') !== ''
      }

      getCurrentSizeSelection() {
        const variantSelects = this.variantSelectors
        if (!variantSelects) return null

        // Check for checked radio input with name starting with "Size"
        const sizeInput = variantSelects.querySelector('input[type="radio"][name^="Size"]:checked')
        if (sizeInput) {
          return sizeInput.value
        }

        // Fall back to dropdown select with name starting with "Size"
        const sizeSelect = variantSelects.querySelector('select[name^="Size"]')
        if (sizeSelect) {
          return sizeSelect.value
        }

        return null
      }

      findVariantBySize(productData, sizeValue) {
        if (!productData || !productData.variants) {
          return null
        }

        // If no size selected, return first available variant
        if (!sizeValue) {
          return productData.variants.find((v) => v.available) || productData.variants[0]
        }

        // Find the Size option and get its position
        const sizeOption = productData.options?.find(
          (opt) => opt.name?.toLowerCase() === 'size'
        )

        if (!sizeOption) {
          return productData.variants.find((v) => v.available) || productData.variants[0]
        }

        const optionKey = `option${sizeOption.position}`

        // Normalize function to remove spaces for comparison (handles "M(11)" vs "M (11)")
        const normalize = (str) => str ? str.replace(/\s+/g, '') : ''
        const normalizedSizeValue = normalize(sizeValue)

        // First, try to find an available variant with matching size
        const availableMatch = productData.variants.find(
          (v) => normalize(v[optionKey]) === normalizedSizeValue && v.available
        )
        if (availableMatch) {
          return availableMatch
        }

        // If no available variant, find any variant with matching size (will show as sold out)
        const anyMatch = productData.variants.find((v) => normalize(v[optionKey]) === normalizedSizeValue)
        if (anyMatch) {
          return anyMatch
        }

        // Size doesn't exist on target product, fall back to first available
        const firstAvailable = productData.variants.find((v) => v.available)
        if (firstAvailable) {
          return firstAvailable
        }

        return productData.variants[0] || null
      }

      async fetchProductJson(productUrl) {
        // Extract handle and build JSON URL
        const handle = productUrl.split('/products/').pop()?.split('?')[0]
        if (!handle) return null

        // Check cache first
        if (!this.productJsonCache) {
          this.productJsonCache = {}
        }
        if (this.productJsonCache[handle]) {
          return this.productJsonCache[handle]
        }

        try {
          const response = await fetch(`/products/${handle}.json`)
          if (!response.ok) return null
          const data = await response.json()
          this.productJsonCache[handle] = data.product
          return data.product
        } catch (e) {
          console.error('Failed to fetch product JSON:', e)
          return null
        }
      }

      async resolveCombinedListingUrlAsync(targetProductUrl, shouldFetchFullPage = false) {
        // Get current size selection before fetching
        const currentSize = this.getCurrentSizeSelection()

        // Fetch target product JSON
        const targetProduct = await this.fetchProductJson(targetProductUrl)

        // Build URL with variant parameter
        const params = []
        if (!shouldFetchFullPage) {
          params.push(`section_id=${this.sectionId}`)
        }

        if (targetProduct) {
          const targetVariant = this.findVariantBySize(targetProduct, currentSize)
          if (targetVariant) {
            params.push(`variant=${targetVariant.id}`)
          }
        }

        return `${targetProductUrl}${params.length ? '?' + params.join('&') : ''}`
      }

      get productForm() {
        return this.querySelector(`product-form`)
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`)
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`)
      }

      get variantSelectors() {
        return this.querySelector('variant-selects')
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        )
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        )
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        )
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        )
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section
      }

      // autoSelectSingleAvailableSize() {
      //   const variantSelects = this.variantSelectors
      //   if (!variantSelects) return

      //   // Check if no variant is currently selected OR if selected variant is unavailable
      //   const currentVariant = this.getSelectedVariant(document)
      //   if (currentVariant && currentVariant.available) {
      //     return
      //   }

      //   // Find size fieldsets (could be pills or dropdowns)
      //   const sizeFieldsets = variantSelects.querySelectorAll('fieldset')

      //   sizeFieldsets.forEach((fieldset) => {
      //     const legend = fieldset.querySelector('legend')?.textContent
      //     if (!legend || !legend.toLowerCase().includes('size')) return

      //     // For pill/button type size selectors - check for both disabled class and attribute
      //     const availableInputs = fieldset.querySelectorAll('input[type="radio"]:not(.disabled):not([disabled])')

      //     if (availableInputs.length >= 1) {
      //       // Auto-select the first available size
      //       availableInputs[0].checked = true
      //       availableInputs[0].dispatchEvent(new Event('change', { bubbles: true }))
      //       return
      //     }
      //   })

      //   // Also check for dropdown size selectors
      //   const sizeDropdowns = variantSelects.querySelectorAll('select')
      //   sizeDropdowns.forEach((select) => {
      //     const label = select.closest('.product-form__input')?.querySelector('label')?.textContent
      //     if (!label || !label.toLowerCase().includes('size')) return

      //     const availableOptions = select.querySelectorAll('option:not([disabled])')
      //     // Skip the first option if it's empty/placeholder
      //     const validOptions = Array.from(availableOptions).filter(option => option.value.trim())

      //     if (validOptions.length >= 1) {
      //       // Auto-select the first available size
      //       select.value = validOptions[0].value
      //       validOptions[0].selected = true
      //       select.dispatchEvent(new Event('change', { bubbles: true }))
      //     }
      //   })
      // }
    }
  )
}
